<template>
  <div class="flex flex-col justify-center w-full">
    <CBreadcrumbs
      :breadcrumb="data.breadcrumb"
      :base-url="config.baseUrl"
      base-path="/kategorier/"
      data-test="pdp-breadcrumbs"
      @meta-change="updateBreadcrumbMeta"
    />
    <section class="min-h-full p-4 xl:pt-4 xl:pr-6 xl:pl-6">
      <div
        class="h-10 flex mb-2 justify-between lg:max-w-[1720px] lg:text-lg lg:w-full lg:mt-0 lg:mx-auto title-section"
      >
        <CLoader v-if="pending" duration="1" class="flex lg:max-w-sm lg:h-6" />
        <h2
          v-else
          class="mx-0 my-auto text-sm font-bold leading-6 text-primary-900 lg:text-lg"
          data-test="product-name"
        >
          {{ data.product.name }}
        </h2>
      </div>
      <div
        class="flex flex-col xl:max-w-[1720px] xl:flex-row xl:w-full xl:-my-0 xl:mx-auto min-h-[575px]"
        :class="{ 'xl:justify-center xl:bg-white': !hasRelations }"
      >
        <div
          class="flex flex-col flex-1 bg-white md:flex-row flex-grow-[2]"
          :class="{ 'xl:max-w-screen-lg': !hasRelations }"
        >
          <div class="relative flex flex-col flex-1 p-4 pr-3 bg-white rounded xl:pr-3 xl:p-5">
            <div class="z-50 h-12">
              <ClientOnly>
                <LazyCImage
                  v-if="!pending && brandImage && brandImage.assetUrl"
                  class="brand-logo"
                  :class="{ hidden: brandImageError }"
                  :src="brandImage.assetUrl"
                  :alt="brandImage.brand || 'brand image'"
                  height="40"
                  width="112"
                  :params="{ w: '112', h: '40', fit: 'inside' }"
                  :fallback-image-src="brandImage.assetFallbackUrl"
                  @fallback-image-error="brandImageFallback"
                />
              </ClientOnly>
            </div>
            <div v-if="campaignBadges" class="absolute top-0 right-3 z-[1] w-full">
              <LazyCampaignBadges v-if="campaignBadges" :badges="campaignBadges" />
            </div>
            <div ref="imageContainer" class="box-border relative flex flex-col items-center">
              <CLoader
                v-if="pending"
                duration="1"
                :style="{
                  width: imageDisplaySize + 'px',
                  height: imageDisplaySize + 'px',
                  maxWidth: imageDisplaySize + 'px',
                }"
              />
              <CImage
                v-else
                :src="data.productImageSrc"
                :alt="data.variant.name || 'Missing alternative text for image'"
                :width="imageDisplaySize"
                :height="imageDisplaySize"
                :params="{ w: imageDisplaySize, h: imageDisplaySize, fit: 'contain' }"
                class="product-image cursor-zoom-in"
                @click="showLightbox = true"
              />
              <LazyCImageCarousel
                v-if="thumbnails.length > 1"
                :images="thumbnails"
                :carousel-image-count="carouselImageCount"
                :thumbnail-size="thumbnailDisplaySize"
                :thumbnail-margin="8"
                :alt-text="data.variant.name"
                @click="setProductImageSrc([$event])"
              />
            </div>
            <div v-if="hasWarnings" class="flex items-end px-6 grow">
              <button class="flex align-center" @click="showWarnings?.()">
                <CIcon name="warning_amber" class="mr-1 text-orange-500" />
                <span class="text-sm font-semibold underline">{{ t('see_warning_labels') }}</span>
              </button>
            </div>
          </div>
          <div
            class="variant-section flex-1 box-border p-4 pl-3 xl:pl-3 xl:p-5 my-px bg-white rounded xl:my-0 xl:mx-px min-width-[362px]"
            data-test="pdp-variant-section"
          >
            <div class="flex flex-col justify-between mb-2">
              <div class="flex justify-start mb-4 gap-x-2">
                <CText el="span" variant="md" class="whitespace-nowrap">
                  {{ t('art_nr') }}
                  <CLoader v-if="pending" duration="1" class="inline-block w-48 ml-1" />
                  <b v-else data-test="variant-number">{{ data.variant.articleNumber }}</b>
                </CText>
                <DiscontinuedLabel v-if="data.variant.discontinued" />
              </div>
              <CLoader v-if="pending" duration="1" class="h-4 max-w-xs" />
              <h4 v-else class="c-heading c-heading--4 text-primary-900 lg:mr-4" data-test="variant-name">
                {{ data.variant.name }}
              </h4>
              <div
                v-if="isBuyable"
                class="mx-0 my-4 text-xs text-gray-800 lg:flex lg:flex-col lg:justify-between lg:mt-5"
                data-test="variant-price"
              >
                <CLoader v-if="isPriceLoading" duration="0.5" class="h-4 lg:text-xl" />
                <span v-else-if="data.variant.price" class="flex items-baseline">
                  <PriceIndicator
                    :price="data.variant.price.netPrice"
                    :price-unit="salesUnits?.unit || salesPriceUnit"
                    :sales-units="salesUnits"
                    :price-type="data.variant.price.priceType"
                    :pre-campaign-price="data.variant.price.preCampaignPrice"
                    :show-detailed-previous-price="true"
                    :vat-text="brandConfig.additions.displayVatText ? t('price.excl_vat') : ''"
                    :ask-for-price="data.variant.price.askForPrice"
                    text-variant="lg"
                  />
                </span>
                <LazyProductDetailPageVariantPrice
                  v-if="isPriceAvailable"
                  :is-price-loading="isPriceLoading"
                  :current-variant-price="data.variant.price"
                  :sales-units="salesUnits"
                />
                <BracketPricing
                  v-if="data.variant.price?.hasBracketPrices"
                  class="mt-5"
                  :article-number="data.variant.articleNumber!"
                  :show-header="false"
                  :sales-units="data.variant?.webSalesUnit"
                />
              </div>
            </div>

            <client-only>
              <div>
                <div class="flex flex-col items-start gap-4 mb-8" data-test="pdp__add-to-cart">
                  <LazyAddToCart
                    v-if="isLoggedIn && isBuyable"
                    :common-variant-attributes="data.product.commonVariantAttributes"
                    :variant="data.variant"
                    :is-favorites-icon-visible="true"
                    size="medium"
                    button-shape="rounded"
                    :layout="
                      configuratorRoute ? 'add-to-cart--PDP-configurator-mobile' : 'add-to-cart--PDP-stack-mobile'
                    "
                    quote-layout="extended"
                    @click.prevent.stop
                    @quantity-update="(value) => (quantity = value)"
                  />
                  <CLink
                    v-if="configuratorRoute"
                    :to="configuratorRoute"
                    data-test="configurator-router-button"
                    class="!font-semibold"
                  >
                    {{ t('go_to_product_configurator') }}
                  </CLink>
                  <ReplacementAlert
                    v-if="data.variant.discontinued"
                    :replacement="replacement"
                    @add="addItemToCart({ ...$event, quantity }, config.gtm.REPLACEMENT_PRODUCT)"
                  />
                </div>

                <LazySelectDeliveryProductDetailPage
                  v-if="isLoggedIn && data.variant.filterTags"
                  :stock="data.variant.stock"
                  :filter-tags="data.variant.filterTags"
                />
                <CButton
                  v-if="isLoggedIn"
                  :is-disabled="!data.variant.stock"
                  variant="secondary"
                  data-test="store-stock-status-btn"
                  class="w-full mt-4 sm:w-auto"
                  @click="setStockInformationData({ data: data.variant, type: 'store_stock_status' })"
                >
                  {{ t('show') }} {{ t('store_stock').toLowerCase() }}
                </CButton>
              </div>
            </client-only>
          </div>
        </div>
        <client-only>
          <transition name="slide-fade">
            <div
              v-if="hasRelations"
              class="flex flex-col flex-1 xl:min-w-[494px] mt-4 space-y-1 xl:ml-1 xl:mt-0 xl:max-w-[631px]"
            >
              <div class="flex flex-col items-center pb-4 space-y-2 bg-white rounded grow">
                <template v-for="relation in upperSectionRelations" :key="relation.label">
                  <LazyProductDetailPageRelationsSection
                    :ref="(el) => (relationsSections[config.gtm.PRODUCT_RELATIONS_UPPER_LIST] = el)"
                    :tracking-position="config.gtm.PRODUCT_RELATIONS_UPPER_LIST"
                    :relation="relation"
                    :is-price-loading="isPriceLoading"
                    :is-stock-loading="isStockLoading"
                    :is-upper-section="true"
                  />
                  <CButton
                    v-if="relation?.scrollTarget"
                    data-test="show-all-relations-btn"
                    class="hidden mt-2 mb-4 lg:block"
                    @click="scrollToElement(relation?.scrollTarget)"
                    >{{ t('show_everything') }}</CButton
                  >
                </template>
              </div>
            </div>
          </transition>
        </client-only>
      </div>
    </section>
    <ProductDetailPageSpecification
      :climate="data.climate"
      :product="data.product"
      :variant="data.variant"
      :attributes-to-hide="attributesToHide"
      @show-warnings="showWarnings = $event"
    />

    <div class="flex-grow bg-gray-100">
      <div class="my-6 mx-4 md:max-w-[1720px] md:mx-auto md:py-0 md:px-20 md:box-content">
        <div class="flex items-center mb-4 md:mb-5">
          <h2 class="text-sm font-bold text-primary-900 md:text-lg">
            {{ t('products_variants') }}
          </h2>
        </div>
        <VariantTable
          v-if="data.product.variants && data.variant"
          :layout="'pdp'"
          :product="data.product"
          :current-id="data.variant.articleNumber"
        />
      </div>
    </div>

    <client-only>
      <div class="grow mx-4 md:w-full my-6 md:mx-auto md:max-w-[1720px] md:py-0 md:px-20">
        <LazyProductDetailPageRelationsSection
          v-if="relations.reservdel"
          :ref="(el) => (relationsSections[config.gtm.PRODUCT_RELATIONS_SPAREPART_LOWER_LIST] = el)"
          :tracking-position="config.gtm.PRODUCT_RELATIONS_SPAREPART_LOWER_LIST"
          :relation="relations.reservdel"
          :is-price-loading="isPriceLoading"
          :is-stock-loading="isStockLoading"
        />
        <LazyProductDetailPageRelationsSection
          v-if="relations.kompletteraMed && mq.largeUp"
          :ref="(el) => (relationsSections[config.gtm.PRODUCT_RELATIONS_COMPLEMENTARY_LOWER_LIST] = el)"
          :tracking-position="config.gtm.PRODUCT_RELATIONS_COMPLEMENTARY_LOWER_LIST"
          :relation="relations.kompletteraMed"
          :is-price-loading="isPriceLoading"
          :is-stock-loading="isStockLoading"
        />
        <LazyProductDetailPageRelationsSection
          v-if="relations.tillbehor && relations.tillbehor.items?.length"
          :ref="(el) => (relationsSections[config.gtm.PRODUCT_RELATIONS_ACCESSORIES_LOWER_LIST] = el)"
          :tracking-position="config.gtm.PRODUCT_RELATIONS_ACCESSORIES_LOWER_LIST"
          :relation="relations.tillbehor"
          :is-price-loading="isPriceLoading"
          :is-stock-loading="isStockLoading"
        />
      </div>
    </client-only>

    <LazyStickyBuyBar
      v-if="isLoggedIn && data.variant.price"
      :current-variant="data.variant"
      :price-type="data.variant.price.priceType"
      :sales-units="salesUnits"
      :net-price="data.variant.price.netPrice"
      :sales-price-unit="salesPriceUnit"
      :ask-for-price="data.variant.price.askForPrice"
      :delivery-method="getDeliveryNameFromId(getSelectedDeliveryId).value"
      :is-visible="isStickyHeaderVisible"
      :is-purchasable="isPurchasable"
      :order-multiple="getOrderMultiple(getStoreIdForSelectedDeliveryId, data.variant.stock)"
      @buy-click="addToCartFromStickyHeader"
      @keydown="handleKeyRestrictions($event, { allowedKeys: [',', '.'], allowLetter: false })"
    />
    <LazyStockInformationModal
      v-if="stockInformationData"
      :stock-information-data="stockInformationData"
      :is-purchasable="stockInformationData.isPurchasable"
      @close="setStockInformationData(null)"
    />
    <LazyCLightbox
      v-if="showLightbox"
      :images="thumbnails"
      :selected-image="selectedImage"
      :carousel-image-count="carouselImageCount"
      :thumbnail-size="thumbnailDisplaySize"
      @close="showLightbox = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { VNodeRef } from '#app/compat/capi';
import type { Product, Variant, ProductRelations, ProductRelationItem } from '@/types/products';
import type { Breadcrumb } from '@/types/page';
import type { DeliveryId } from '@/stores/delivery';
import gql from '@/gql/pdp';
import {
  useCampaignStore,
  useCartStore,
  useDeliveryStore,
  useStoresStore,
  useUiStore,
  useUserStore,
  useBrandStore,
  useProductStore,
} from '@/stores';
import { OrderType } from '@/config/delivery-config';
import { Sequence } from '~/types/products';

defineRouteRules({
  swr: 60 * 10,
  cache: {
    base: 'cache-storage',
  },
});

type Purchasable = {
  store: boolean;
  online: boolean;
};

const { mq } = useMediaQueries();
const { t } = useI18n();
const { public: config } = useRuntimeConfig();
const { $sentry, $tracking, $apiGateway, $sequenceService } = useNuxtApp();
const route = useRoute();

const uiStore = useUiStore();
const deliveryStore = useDeliveryStore();
const cartStore = useCartStore();
const campaignStore = useCampaignStore();
const userStore = useUserStore();
const storesStore = useStoresStore();
const productStore = useProductStore();
const { sortSequence, disabledFacets } = storeToRefs(productStore);

const { brandConfig } = useBrandStore();
const { selectedLocations, getSelectedDeliveryId, getCurrentStockLocation, getStockInput } = storeToRefs(deliveryStore);
const { overlayComponents } = storeToRefs(uiStore);
const { campaigns: activeCampaigns, assortments } = storeToRefs(campaignStore);
const { isLoggedIn, isAuthenticating } = storeToRefs(userStore);
const { getStoreIdForSelectedDeliveryId } = storeToRefs(storesStore);
const { getDeliveryNameFromId, getOrderTypeForDeliveryId } = deliveryStore;
const { addItemToCart } = cartStore;

const thumbnailDisplaySize = 46;
const breadcrumbMeta = ref(undefined);
const isPriceLoading = ref(false);
const isStockLoading = ref(false);
const brandImageError = ref(false);
const relations = ref<ProductRelations>({});
const isBuyable = ref(false);
const hasTracked = ref(false);
const showLightbox = ref(false);
const stockInformationData = ref(null);
const imageContainer = ref<null | VNodeRef>(null);
const relationsSections = ref<{ [key: string]: any }>([]);
const showWarnings = ref<null | Function>(null);
const quantity = ref(1);

provide('isLoading', ref({ stock: isStockLoading, price: isPriceLoading }));

const slug = computed(() => route.params.slug);
const isStickyHeaderVisible = computed(() => !!overlayComponents.value.stickyHeader);

type Data = { variant: Variant; product: Product; breadcrumb: Breadcrumb; productImageSrc: any[] };

const initialData: Data = {
  product: { variants: [] },
  variant: {},
  productImageSrc: [],
  breadcrumb: { name: '', parents: [] },
};

const getSequencesByBreadcrumb = async (breadcrumb: Breadcrumb) => {
  let categorySlug;

  if (config.brand === 'dahl' && breadcrumb.parents) {
    categorySlug = breadcrumb.parents[2]?.slug ?? breadcrumb.slug;
  } else {
    categorySlug = breadcrumb.slug;
  }

  if (categorySlug) {
    sortSequence.value = [];

    try {
      const { data } = (await $sequenceService.getSequencesByCategory(categorySlug.split('/').join('-'))) as {
        data: Sequence;
      };
      sortSequence.value = data?.sequence || [];
      disabledFacets.value = data?.disabled || [];
    } catch (error) {
      $sentry.captureException(error);
    }
  }
};

const { data, pending } = await useAsyncData(
  `pdp-${slug}`,
  async (): Promise<Data> => {
    try {
      const { data } = (await $apiGateway.post('/graphql', {
        query: gql.base,
        variables: {
          slug: slug.value,
        },
      })) as any;

      const product = data.data.product;

      if (!product || !product.requestedVariant) {
        // eslint-disable-next-line no-throw-literal
        throw {
          response: {
            status: 404,
          },
          errors: data.errors,
        };
      }

      if (!product.breadcrumb) {
        // eslint-disable-next-line no-throw-literal
        throw {
          response: {
            status: 404,
          },
          errors: 'Breadcrumb missing',
          config,
        };
      }

      const breadcrumb = transformBreadcrumb(product.breadcrumb, product.requestedVariant?.name);
      await getSequencesByBreadcrumb(product.breadcrumb);

      return {
        product,
        breadcrumb,
        variant: product.requestedVariant,
        productImageSrc: product.requestedVariant.images,
      };
    } catch (err: any) {
      $sentry.captureException(err);
      const statusCode = err?.response?.status || 500;

      throw showError({ statusCode, message: 'Failed to get product' });
    }
  },
  {
    lazy: true,
    default: () => initialData,
  },
);

onBeforeUnmount(() => {
  data.value = initialData;
  productStore.clearSequenceData();
});

const salesUnits = computed(() => data.value.variant?.webSalesUnit);
const salesPriceUnit = computed(() => data.value.variant?.price?.salesPriceUnit);
const discontinued = computed(() => data.value.variant?.discontinued);

provide('unit', ref(salesUnits?.unit || salesPriceUnit));
provide('discontinued', ref(discontinued));

const selectedImage = computed(() => {
  if (!Array.isArray(data.value.productImageSrc) || !data.value.productImageSrc.length) return undefined;

  return data.value.productImageSrc[0];
});

const upperSectionRelations = computed(() => {
  const { kompletteraMed, liknandeArtiklar, tillbehor } = relations.value;

  type Relation = {
    label: string;
    items: ProductRelationItem[];
    scrollTarget?: string;
    maxItems: number;
  };

  const upperRelations: Relation[] = [];

  if (kompletteraMed) {
    const maxItems = 3;
    const { label, items } = kompletteraMed;
    const relation: Relation = {
      label,
      items,
      maxItems,
    };
    if (items?.length > maxItems) relation.scrollTarget = config.gtm.PRODUCT_RELATIONS_COMPLEMENTARY_LOWER_LIST;
    upperRelations.push(relation);
  }

  if (liknandeArtiklar) {
    upperRelations.push({
      label: liknandeArtiklar.label,
      items: liknandeArtiklar.items,
      maxItems: 1,
    });
  }

  // Only add "tillbehör" if there are no other relations
  if (upperRelations.length === 0 && tillbehor) {
    const maxItems = 3;
    const { label, items } = tillbehor;
    const relation: Relation = {
      label,
      items,
      maxItems,
    };
    if (items?.length > maxItems) relation.scrollTarget = config.gtm.PRODUCT_RELATIONS_ACCESSORIES_LOWER_LIST;
    upperRelations.push(relation);
  }

  return upperRelations;
});

const hasRelations = computed(() => {
  return upperSectionRelations.value.length > 0;
});

const campaignBadges = computed(() => {
  if (!activeCampaigns.value?.length || !data.value.product.requestedVariant?.campaignIds?.length) {
    return undefined;
  }
  const campaignsAndAssortments = [...activeCampaigns.value, ...assortments.value];
  let campaignBadges;
  try {
    campaignBadges = getBadges(campaignsAndAssortments, data.value.product.requestedVariant?.campaignIds);
  } catch (error) {
    $sentry.captureException(error);
  }

  return campaignBadges;
});

const attributesToHide = computed(() => brandConfig.filterOnlyFacetNames ?? []);

const isPriceAvailable = computed(() => checkIfPriceIsAvailable(data.value.variant.price));

const title = computed(() => {
  const brandTitle = t('head.plp_pdp_title_suffix');
  if (!data.value.variant.name || !data.value.product.name) {
    return brandTitle;
  }
  return `${data.value?.variant.name} - ${data.value.product.name} - ${brandTitle}`;
});

const productDescription = computed(() => data.value.product.text || data.value.variant.description);

const thumbnails = computed(() => (data.value.variant.images?.length ? data.value.variant.images : []));

const smallScreen = computed(() => mq.value.xsmall || mq.value.small);

const imageDisplaySize = computed(() => 392);

const carouselImageCount = computed(() => {
  if (smallScreen.value) {
    return 3;
  }
  return 4;
});

const brandImage = computed(() => (data.value.variant.brands?.length ? data.value.variant.brands[0] : null));

const replacement = computed(() => data.value.variant.relations?.ersattningsartikel?.articles?.[0]);

const configuratorRoute = computed(() => {
  const { variants } = data.value.product;
  if (!variants?.length || !variants[0].configurators?.length) return;
  return {
    name: 'konfigurator',
    query: { instance: variants[0].configurators[0].slug },
  };
});

const product = computed(() => data.value.product);

const canonicalProductUrl = computed(() => {
  return getCanonicalVariant(data.value);
});

const hasWarnings = computed(() => !!(data.value.variant.warnings && data.value.variant.warnings.length));

const isPurchasable = computed(() => isPurchasableOnline(data.value.variant, getCurrentStockLocation.value));

const isPurchasablePerDelivery = computed(() => {
  const result = Object.keys(selectedLocations.value).reduce(
    (obj, currId: DeliveryId) => {
      obj[currId] = isPurchasableOnline(data.value.variant, selectedLocations.value[currId]);
      return obj;
    },
    {} as {
      XXX: Purchasable;
      DBI: Purchasable;
      EXB: Purchasable;
      EXH: Purchasable;
    },
  );
  return result;
});

const isReadyToFetchGqlData = computed(() => {
  return (
    isLoggedIn.value &&
    getSelectedDeliveryId.value &&
    !!selectedLocations.value[getSelectedDeliveryId.value].length &&
    !!data.value.variant.variantSlug
  );
});

const track = () => {
  if (isAuthenticating.value || hasTracked.value || JSON.stringify(data.value) === JSON.stringify(initialData)) return;
  $tracking.emit('productDetailView', data.value.product);
  updateVariantHistory(data.value.variant, config.brand);
  hasTracked.value = true;
};

const getAuthedGqlData = async ({ skipStock = false, skipPrice = false } = {}): Promise<void> => {
  if (process.server) return;

  isStockLoading.value = !skipStock;
  isPriceLoading.value = !skipPrice;

  const {
    data: { data: responseData },
    error,
  } = (await $apiGateway.post('/graphql', {
    query: gql.authenticated,
    variables: {
      articleNumber: data.value.variant.articleNumber,
      deliveryId: getSelectedDeliveryId.value,
      stockInput: getStockInput.value,
      slug: data.value.variant.variantSlug,
      skipStock,
      skipPrice,
      locationId: getCurrentStockLocation.value,
    },
    operationName: 'getPDPLoggedIn',
  })) as any;

  if (error || !responseData) return;

  const newVariants = data.value.product.variants?.map((variant, index) => ({
    ...variant,
    ...responseData.product.variants[index],
  }));

  data.value.product.variants = newVariants;

  if (responseData?.variant?.webSalesUnit) {
    data.value.variant.webSalesUnit = responseData.variant.webSalesUnit;
  }

  if (responseData?.variant?.purchaseConstraints && responseData?.variant?.stockedAt) {
    data.value.variant.purchaseConstraints = responseData.variant.purchaseConstraints;
    data.value.variant.stockedAt = responseData.variant.stockedAt;
  }

  if (!skipPrice && responseData?.product?.variants) {
    setPrice(responseData.product.variants);
    isPriceLoading.value = false;
  }

  if (!skipStock && responseData?.product?.variants) {
    setStock(responseData.product.variants);
    isStockLoading.value = false;
  }

  const hasRelations = Object.values({ ...responseData?.variant?.relations }).length > 0;

  if (hasRelations) {
    setRelations(responseData.variant.relations);
  }
};

const setRelations = (currentRelations: ProductRelations) => {
  const transformedRelations = {} as ProductRelations;
  Object.entries(currentRelations).forEach(([relationType, relation]) => {
    if (!relation) return;

    let filteredVariants = [];

    if (relation.articles) {
      filteredVariants = relation.articles.filter((variant) => {
        const filtered = filterRelatedArticlesByType(data.value.variant.stock?.stockType, relationType)(variant);

        if (!filtered) return false;

        if (relationType === 'kompletteraMed') {
          const selectedStockLocation = variant.stock?.locations.find(
            (location) => location.locationId === getStoreIdForSelectedDeliveryId.value,
          );
          return selectedStockLocation?.quantity > 0;
        }
        return true;
      });
    }

    if (!filteredVariants?.length && !relation.categories?.length) return;

    const categories = relation.categoryBreadcrumbs?.length
      ? relation.categoryBreadcrumbs.filter((item) => !!item)
      : [];

    transformedRelations[relationType] = {
      label: relation.label === 'Reservdel' ? t('spare_parts') : relation.label,
      items: [...categories, ...filteredVariants],
    };
  });
  relations.value = transformedRelations;
};

const setPrice = (variants: Variant[]) => {
  const updatedVariants = [];
  for (const currentVariant of data.value.product.variants) {
    const incomingData = variants.find((item) => item.articleNumber === currentVariant.articleNumber);
    if (!incomingData) continue;
    if (incomingData.articleNumber === data.value.variant.articleNumber) {
      data.value.variant.price = incomingData.price;
    }
    updatedVariants.push({
      ...currentVariant,
      price: incomingData.price,
    });
  }
  data.value.product.variants = updatedVariants;
};

const setStock = (variants: Variant[]) => {
  const updatedVariants = [];
  for (const currentVariant of data.value.product.variants) {
    const incomingData = variants.find((item) => item.articleNumber === currentVariant.articleNumber);
    if (!incomingData) continue;
    if (incomingData.articleNumber === data.value.variant.articleNumber && incomingData.stock) {
      data.value.variant.stock = incomingData.stock;
    }
    const updatedVariant = {
      ...currentVariant,
      stock: incomingData.stock,
    };
    updatedVariants.push(updatedVariant);
  }
  data.value.product.variants = updatedVariants;
  isBuyable.value = isVariantBuyable(data.value.variant);
};

const brandImageFallback = () => {
  brandImageError.value = true;
};

const addToCartFromStickyHeader = (payload: any) => {
  addItemToCart(
    {
      ...payload.variant,
      quantity: payload.quantity,
      deliveryId: getSelectedDeliveryId.value,
      filterTags: payload.variant.filterTags || [],
    },
    config.gtm.STICKY_HEADER,
  );
};

const setProductImageSrc = (image: any) => {
  data.value.productImageSrc = image;
};

const updateBreadcrumbMeta = (meta: any) => {
  breadcrumbMeta.value = meta;
};

const scrollToElement = (refName: string) => {
  const element = relationsSections.value[refName]?.$el as HTMLElement;
  if (element) {
    const top = element.offsetTop;
    window.scrollTo({ top, behavior: 'smooth' });
  }
};

const setStockInformationData = (payload: any) => {
  if (payload) {
    const isPurchasable = isPurchasableOnline(payload.data, getCurrentStockLocation.value);
    payload.isPurchasable = isPurchasable;
    if (!isPurchasable.online) payload.type = 'global_stock_status';
  }

  stockInformationData.value = payload;
};

const getMeta = () => {
  const image = data.value.productImageSrc[0]?.assetUrl;
  const url = config.baseUrl + route.path;
  const brand =
    data.value.variant.brands && data.value.variant.brands.length > 0
      ? data.value.variant.brands[0]
      : ({} as { brand?: string; assetUrl?: string });

  const link: any[] = [];

  const canonicalVarientSlug: string | undefined = getCanonicalVariant(data.value);
  if (canonicalVarientSlug) {
    link.push({ rel: 'canonical', href: `${config.baseUrl}/produkt/${canonicalVarientSlug}` });
  }

  const script = [
    {
      type: 'application/ld+json',
      json: {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: data.value.variant.name,
        description: productDescription.value,
        itemCondition: 'NewCondition',
        gtin: data.value.variant.ean,
        sku: data.value.variant.articleNumber,
        url,
        brand: {
          '@type': 'Brand',
          name: brand.brand,
          logo: brand.assetUrl,
        },
      },
    },
  ] as any[];

  if (breadcrumbMeta.value) {
    script.push(breadcrumbMeta.value);
  }

  if (image) {
    script[0].json.image = image;
    link.push({ rel: 'preload', as: 'image', href: `${image}?w=${imageDisplaySize.value}` });
  }

  return {
    link,
    title: title.value,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: productDescription.value,
      },
    ],
    script,
  };
};

const getCanonicalLinkMeta = (variantSlug?: string) => {
  const link: any[] = [];

  if (variantSlug) {
    link.push({
      rel: 'canonical',
      href: `${config.baseUrl}/produkt/${variantSlug}`,
    });
  }

  return {
    link,
  };
};

const getCanonicalVariant = (data?: Data): string | undefined => {
  // Canonical ref should point to a specific product variant.
  return data?.variant?.variantSlug;
};

watch(
  [getSelectedDeliveryId, isLoggedIn, selectedLocations, () => data.value.variant.variantSlug],
  ([newSelectedDeliveryId], [oldSelectedDeliveryId]) => {
    const newOrderType: OrderType = getOrderTypeForDeliveryId(newSelectedDeliveryId).value;
    const oldOrderType: OrderType = getOrderTypeForDeliveryId(oldSelectedDeliveryId).value;

    const isOrderTypeSame: boolean = newOrderType === oldOrderType;

    const skipRefetch =
      brandConfig.centralStorageId &&
      isOrderTypeSame &&
      newSelectedDeliveryId !== oldSelectedDeliveryId &&
      oldSelectedDeliveryId !== undefined;

    if (isReadyToFetchGqlData.value && !skipRefetch) {
      getAuthedGqlData();
    }

    track();
  },
);

watch(
  () => data.value,
  () => {
    track();
  },
);

watch(
  () => canonicalProductUrl.value,
  () => {
    useHead(getCanonicalLinkMeta(canonicalProductUrl.value));
  },
);

useHead(getMeta());

provide('isPurchasablePerDelivery', isPurchasablePerDelivery);
provide('isStockLoading', isStockLoading);
provide('product', product);
</script>

<script lang="ts">
export default defineComponent({
  name: 'ProductDetailPage',
});
</script>

<style lang="scss" scoped>
// The css related to .product-image is to maintain the image size during loading to minimize layout shift
:deep(.product-image) {
  img.lazyloading {
    @apply w-full h-full;
  }

  &:has(img.lazyloaded) {
    @apply my-8 sm:min-w-[302px] max-w-[392px];
    width: auto !important;
    height: auto !important;
  }
}

:deep(.brand-logo) {
  img {
    @apply h-auto;
  }
}
:deep(.title-section) {
  img {
    @apply object-contain object-right-bottom w-[inherit];
  }
}
.slide-fade-enter-active {
  transition: all 0.2s ease-out;
}

.slide-fade-enter-from {
  transform: translateX(20px);
  opacity: 0;
}
</style>
